import { isNil } from 'ramda';
import { __, _n, sprintf } from '@wordpress/i18n';
import { queryClient, queryKeys } from '../../lib/query-client';
import RecSessionService from '../../service/rec-session-service';
import ProjectService from '../../service/project-service';
import displayNetworkError from '../../helpers/displayNetworkError';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';
import handleErrorAndDisplay from '../../helpers/handleErrorAndDisplay';
import { buildRecSessionUpdateBody } from '../../helpers/recSession';
import { EMPTY_SELECTION } from '../../helpers/selection';
import { storeSet } from '../index';

const allTrackIds = (session) => new Set([
    ...(session?.coreLayer?.tracks || []).map(t => t.id),
    ...(session?.auxLayers || []).flatMap(l => (l.tracks || []).map(t => t.id)),
]);

export const makeRecSessionActions = (set, get, { cacheRecSessionAudioImpl, cleanupRecSessionAudioImpl } = {}) => {
    const fetchRecSessionsImpl = async (projectId) => {
        set('recSessionsLoading', true);
        set('recSessionsError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.recSessions(projectId),
                queryFn: () => RecSessionService.getRecSessionsAsync(projectId),
            });

            set('recSessions', data);
            set('recSessionsLoading', false);
        } catch (error) {
            set('recSessionsError', error);
            set('recSessionsLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    /**
     * Puts a fresh copy of one session in the store. When it is the session on
     * the timeline, the clips it does not have yet are cached, so a new upload
     * is heard right away; the ones already loaded are left alone.
     */
    const replaceSessionImpl = async (projectId, session, { reloadAudio = true } = {}) => {
        set('recSessions', get('recSessions').map(s => s.id === session.id ? session : s));

        if (reloadAudio && get('selectedRecItemId') === session.id) {
            await cacheRecSessionAudioImpl?.(projectId, session);
        }

        await queryClient.invalidateQueries({ queryKey: queryKeys.recSessions(projectId) });
    };

    const saveAuxLayersImpl = async (projectId, recSessionId, auxLayers) => {
        const session = get('recSessions').find(s => s.id === recSessionId);
        if (!session) return null;

        const updated = await RecSessionService.updateRecSessionAsync(
            projectId,
            recSessionId,
            buildRecSessionUpdateBody(session, { auxLayers })
        );

        // Layers changed, clips did not: the timeline keeps the audio it has.
        await replaceSessionImpl(projectId, updated, { reloadAudio: false });

        return updated;
    };

    return {
        fetchRecSessions: fetchRecSessionsImpl,

        /**
         * @param {object} [options]
         * @param {string} [options.targetLayerId] the aux layer the track belongs
         *   to — a collaborator uploads into their own layer. The upload endpoint
         *   only knows the layer's session type, so a track that lands anywhere
         *   else is moved there afterwards.
         */
        uploadRecSessionTrack: async (projectId, recSessionId, formData, type, subType, { targetLayerId } = {}) => {
            try {
                const before = allTrackIds(get('recSessions').find(s => s.id === recSessionId));

                const body = new FormData();
                body.append('file', formData.file);

                const payload = {};
                if (formData.title) payload.title = formData.title;
                if (formData.label) payload.label = formData.label;
                body.append('Payload', JSON.stringify(payload));

                const queryParams = [['type', type]];
                if (subType) queryParams.push(['subType', subType]);

                let updatedSession = await RecSessionService.uploadTrackAsync(projectId, recSessionId, body, queryParams);

                if (targetLayerId && updatedSession?.auxLayers) {
                    const target = updatedSession.auxLayers.find(l => l.id === targetLayerId);
                    const misplaced = updatedSession.auxLayers
                        .filter(l => l.id !== targetLayerId)
                        .flatMap(l => l.tracks || [])
                        .filter(t => !before.has(t.id));

                    if (target && misplaced.length > 0) {
                        await RecSessionService.bulkUpdateTracksAsync(projectId, recSessionId, {
                            items: misplaced.map(t => ({ id: t.id, layer_id: targetLayerId })),
                        });
                        updatedSession = await RecSessionService.getRecSessionAsync(projectId, recSessionId);
                    }
                }

                await replaceSessionImpl(projectId, updatedSession);

                handleSuccessAndDisplay(__('Traccia caricata con successo', 'soundbetween'));

                return updatedSession;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        createRecSession: async (projectId, body) => {
            try {
                const newSession = await RecSessionService.createRecSessionAsync(projectId, body);

                set('recSessions', [...get('recSessions'), newSession]);

                await queryClient.invalidateQueries({ queryKey: queryKeys.recSessions(projectId) });

                handleSuccessAndDisplay(__('Registrazione creata con successo', 'soundbetween'));

                return newSession;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /**
         * Invites collaborators into a rec session: each one gets an aux layer
         * of their own, tagged with the instrument they are asked to play.
         *
         * Whoever is not in the project team yet is invited to the project first
         * (with the role and note of the form) — a layer assigned to a user who
         * cannot open the project would be unreachable for them. A user whose
         * invitation fails gets no layer.
         *
         * @param {string} projectId
         * @param {string} recSessionId
         * @param {object} params
         * @param {Array<{user: object, instrument: string}>} params.collaborators
         *   `user` as /users/list or the team returns it
         * @param {string} params.role
         * @param {string} [params.note]
         */
        inviteRecSessionCollaborators: async (projectId, recSessionId, { collaborators, role, note }) => {
            try {
                const session = get('recSessions').find(s => s.id === recSessionId);
                if (!session || !collaborators?.length) return null;

                const team = await queryClient.fetchQuery({
                    queryKey: queryKeys.projectTeam(projectId),
                    queryFn: () => ProjectService.getProjectTeamAsync(projectId),
                });
                const inTeam = new Set((team || []).map(row => row.userId).filter(Boolean));
                const currentUserId = get('userData')?.id;

                const toInvite = [...new Set(collaborators.map(c => c.user.userId))]
                    .filter(userId => !inTeam.has(userId) && userId !== currentUserId);

                const failed = new Set();

                if (toInvite.length > 0) {
                    const response = await ProjectService.createProjectTeamInvitationsBulkAsync(projectId, {
                        invitations: toInvite.map(userId => ({ user_id: userId, role, note: note || null })),
                    });
                    const results = response?.results ?? response ?? [];

                    results.filter(r => !isNil(r.error)).forEach(r => {
                        failed.add(r.userId);
                        handleErrorAndDisplay(r.error);
                    });

                    storeSet('refetchProjectTeam', projectId);
                }

                const existing = new Set((session.auxLayers || []).map(l =>
                    `${l.assignedUser?.userId}:${l.sessionType}`));

                const newLayers = collaborators
                    .filter(c => !failed.has(c.user.userId))
                    .filter(c => !existing.has(`${c.user.userId}:${c.instrument}`))
                    .map(c => ({
                        sessionType: c.instrument,
                        assignedUser: { userId: c.user.userId },
                        volume: 100,
                        tracks: [],
                    }));

                if (newLayers.length === 0) return session;

                const updated = await saveAuxLayersImpl(projectId, recSessionId, [
                    ...(session.auxLayers || []),
                    ...newLayers,
                ]);

                handleSuccessAndDisplay(sprintf(
                    _n('%d collaboratore invitato', '%d collaboratori invitati', newLayers.length, 'soundbetween'),
                    newLayers.length
                ));

                return updated;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /** Deletes a whole session: main track, collaborators and their tracks. */
        deleteRecSession: async (projectId, recSessionId) => {
            try {
                await RecSessionService.deleteRecSessionAsync(projectId, recSessionId);

                set('recSessions', get('recSessions').filter(s => s.id !== recSessionId));

                if (get('selectedRecItemId') === recSessionId) {
                    cleanupRecSessionAudioImpl?.();
                    set('selectedRecItemId', null);
                    set('selectedRecMemberId', null);
                    set('selection', EMPTY_SELECTION);
                }

                await queryClient.invalidateQueries({ queryKey: queryKeys.recSessions(projectId) });

                handleSuccessAndDisplay(__('Sessione eliminata', 'soundbetween'));
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /** Empties the main track; the collaborators' layers are kept. */
        removeRecSessionMainTrack: async (projectId, recSessionId) => {
            try {
                const session = get('recSessions').find(s => s.id === recSessionId);
                if (!session) return null;

                const updated = await RecSessionService.updateRecSessionAsync(
                    projectId,
                    recSessionId,
                    buildRecSessionUpdateBody(session, { coreTracks: [] })
                );

                await replaceSessionImpl(projectId, updated);

                handleSuccessAndDisplay(__('Traccia principale rimossa', 'soundbetween'));

                return updated;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /** Saves the volume of one collaborator's lane (0–100). */
        updateRecSessionLayerVolume: async (projectId, recSessionId, layerId, volume) => {
            try {
                const session = get('recSessions').find(s => s.id === recSessionId);
                const layer = session?.auxLayers?.find(l => l.id === layerId);
                if (!layer || layer.volume === volume) return session ?? null;

                return await saveAuxLayersImpl(
                    projectId,
                    recSessionId,
                    session.auxLayers.map(l => l.id === layerId ? { ...l, volume } : l)
                );
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /** Drops every aux layer of one collaborator from the session. */
        removeRecSessionMember: async (projectId, recSessionId, userId) => {
            try {
                const session = get('recSessions').find(s => s.id === recSessionId);
                if (!session) return null;

                const updated = await saveAuxLayersImpl(
                    projectId,
                    recSessionId,
                    (session.auxLayers || []).filter(l => l.assignedUser?.userId !== userId)
                );

                if (get('selectedRecMemberId') === userId) {
                    set('selectedRecMemberId', null);
                }

                handleSuccessAndDisplay(__('Collaboratore rimosso dalla sessione', 'soundbetween'));

                return updated;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },
    };
};
